/**
 * Ola · Variantes en la tarjeta de producto.
 *
 * Al pasar el cursor por una muestra con foto (snippets/variant-swatches.liquid), la tarjeta muestra
 * la foto de esa variante; al salir vuelve a la variante elegida o a la foto del principio.
 *
 * Horizon deja ocultas en la galería de la tarjeta las fotos de variantes que no son la principal, y
 * cuando solo queda una foto visible su vista previa no puede mostrarlas. Aquí se muestran y ocultan
 * directamente, igual que hace Horizon cuando se elige una variante. Elegir con clic o toque sigue
 * siendo de Horizon (cambia foto, precio, enlace y «Agregar»).
 *
 * Solo con mouse: en pantallas táctiles el toque elige la variante.
 *
 * Al elegir una variante, el precio de la tarjeta pasa a ser el de esa variante (por ejemplo, de
 * «Desde L 287.50» a «L 690.00»). Horizon trae el precio nuevo, pero en estas tarjetas no lo cambia.
 */

const SWATCH = '.ola-swatches label[data-media-id]';

/** @type {WeakMap<HTMLElement, number>} */
const timers = new WeakMap();

/** @type {WeakMap<HTMLElement, string>} */
const initialMedia = new WeakMap();

/**
 * @param {HTMLElement} card
 * @returns {HTMLElement[]}
 */
function variantSlides(card) {
  return Array.from(card.querySelectorAll('slideshow-slide[variant-image]'));
}

/**
 * Deja visible solo la foto de esa variante.
 * @param {HTMLElement} card
 * @param {string | undefined} mediaId
 */
function showMedia(card, mediaId) {
  if (!mediaId) return;

  const slides = variantSlides(card);
  const target = slides.find((slide) => slide.getAttribute('slide-id') === mediaId);
  if (!target) return;

  for (const slide of slides) {
    const visible = slide === target;
    slide.hidden = !visible;
    slide.setAttribute('aria-hidden', visible ? 'false' : 'true');
  }

  // Si la tarjeta también tiene fotos generales, lleva la galería a la foto de la variante.
  const slideshow = /** @type {any} */ (card.querySelector('slideshow-component'));
  slideshow?.select?.({ id: mediaId }, undefined, { animate: false });
}

/**
 * Vuelve a la variante elegida o, si no hay, a la foto con la que se cargó la tarjeta.
 * @param {HTMLElement} card
 */
function restore(card) {
  const checked = /** @type {HTMLInputElement | null} */ (card.querySelector('.ola-swatches input:checked'));
  showMedia(card, checked?.dataset.optionMediaId || initialMedia.get(card));
}

/**
 * @param {Event} event
 * @returns {{ label: HTMLElement, card: HTMLElement } | null}
 */
function swatchFrom(event) {
  if (!(event instanceof PointerEvent) || event.pointerType !== 'mouse') return null;
  if (!(event.target instanceof Element)) return null;

  const label = /** @type {HTMLElement | null} */ (event.target.closest(SWATCH));
  const card = /** @type {HTMLElement | null} */ (label?.closest('product-card'));
  if (!label || !card) return null;

  return { label, card };
}

document.addEventListener('pointerover', (event) => {
  const found = swatchFrom(event);
  if (!found) return;
  const { label, card } = found;

  if (!initialMedia.has(card)) {
    const visible = variantSlides(card).find((slide) => !slide.hidden);
    initialMedia.set(card, visible?.getAttribute('slide-id') ?? '');
  }

  clearTimeout(timers.get(card));
  showMedia(card, label.dataset.mediaId);
});

document.addEventListener('pointerout', (event) => {
  const found = swatchFrom(event);
  if (!found) return;
  const { label, card } = found;

  // Moverse dentro de la misma muestra no cuenta como salir.
  if (event instanceof PointerEvent && event.relatedTarget instanceof Node && label.contains(event.relatedTarget)) {
    return;
  }

  // Una pausa corta para que pasar de una muestra a la siguiente no parpadee.
  clearTimeout(timers.get(card));
  timers.set(
    card,
    window.setTimeout(() => restore(card), 120)
  );
});

document.addEventListener('shopify:product:select', (event) => {
  const picker = event.target;
  if (!(picker instanceof Element) || !picker.matches('swatches-variant-picker-component')) return;
  if (!picker.querySelector('.ola-swatches__form')) return;

  const card = picker.closest('product-card');
  const { promise } = /** @type {{ promise?: Promise<{ detail?: { html?: Document } }> }} */ (
    /** @type {unknown} */ (event)
  );

  promise
    ?.then(({ detail }) => {
      const fresh = detail?.html?.querySelector("product-price [ref='priceContainer']");
      const current = card?.querySelector("product-price [ref='priceContainer']");
      if (fresh && current) current.replaceWith(document.importNode(fresh, true));
    })
    .catch(() => {});
});
